import { StyleSheet, Dimensions } from "react-native";
import color from "@constants/color";
import { fontFamily } from "@utils/styles/fonts";

// Shared design tokens for the revamped (v2) KYC page. These mirror the
// Onboarding flow's visual language (see src/screens/Onboarding/styles.ts):
// white surface, floating-style section labels, D9D9D9 hairline inputs that
// turn primary on focus, and dark upload tiles that flip to a green "filled"
// state once a document is attached.

export const SCREEN_WIDTH = Dimensions.get("window").width;
// Two tiles per row inside a 20px-padded scroll body with a 12px gutter.
export const HALF_TILE = (SCREEN_WIDTH - 40 - 12) / 2;

export const kycColors = {
  primary: color.primary,
  text: color.Gray20,
  muted: color.color_808080,
  border: color.color_D9D9D9,
  placeholder: color.disableFont,
  white: color.white,
  green: color.plain_green,
  greenTint: color.veryLightGreen,
  redTint: color.lightRed,
  error: color.errorRed,
};

export const kycStyles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: color.white,
  },
  // KeyboardAvoidingView wrapper around the scroll area + bottom bar.
  flexFill: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: 20,
    paddingTop: 16,
    paddingBottom: 120,
  },
  section: {
    marginBottom: 24,
  },
  sectionLabel: {
    fontSize: 14,
    fontFamily: fontFamily.semiBold,
    color: color.Gray20,
    marginBottom: 10,
  },
  requiredStar: {
    color: color.primary,
  },
  verifiedRow: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 10,
  },
  verifiedText: {
    marginLeft: 6,
    fontSize: 12,
    fontFamily: fontFamily.semiBold,
    color: color.plain_green,
  },
  // ── Text inputs ────────────────────────────────────────────────────────────
  inputBox: {
    height: 52,
    borderWidth: 1,
    borderColor: color.color_D9D9D9,
    borderRadius: 8,
    paddingHorizontal: 16,
    backgroundColor: color.white,
    flexDirection: "row",
    alignItems: "center",
  },
  inputBoxFocused: {
    borderColor: color.primary,
  },
  inputBoxError: {
    borderColor: color.errorRed,
  },
  inputBoxDisabled: {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  inputText: {
    flex: 1,
    fontSize: 14,
    fontFamily: fontFamily.regular,
    color: color.Gray20,
    padding: 0,
    margin: 0,
    letterSpacing: 0.5,
  },
  errorText: {
    fontSize: 12,
    fontFamily: fontFamily.regular,
    color: color.errorRed,
    marginTop: 6,
    marginLeft: 2,
  },
  // ── Upload tiles ─────────────────────────────────────────────────────────
  uploadRow: {
    flexDirection: "row",
    gap: 12,
  },
  tile: {
    height: 100,
    borderRadius: 8,
    overflow: "hidden",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: color.Gray20,
    gap: 6,
  },
  tileFilled: {
    backgroundColor: color.veryLightGreen,
    borderWidth: 1,
    borderColor: color.plain_green,
  },
  tileIcon: {
    fontSize: 22,
    lineHeight: 26,
    color: color.white,
  },
  tileText: {
    fontSize: 11,
    fontFamily: fontFamily.regular,
    color: color.white,
    textAlign: "center",
    paddingHorizontal: 6,
  },
  tileImage: {
    width: "100%",
    height: "100%",
  },
  tileBadge: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: color.plain_green,
    alignItems: "center",
    justifyContent: "center",
  },
  tileBadgeText: {
    color: color.white,
    fontSize: 12,
    lineHeight: 14,
  },
  tileDelete: {
    position: "absolute",
    top: 6,
    right: 6,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: "rgba(0,0,0,0.55)",
    alignItems: "center",
    justifyContent: "center",
  },
  tileDeleteText: {
    color: color.white,
    fontSize: 14,
    lineHeight: 16,
  },
  // ── Sticky submit bar ──────────────────────────────────────────────────────
  bottomBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: color.white,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: color.lineColor,
  },
  primaryButton: {
    backgroundColor: color.primary,
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  primaryButtonDisabled: {
    backgroundColor: color.lighterBlack,
  },
  primaryButtonText: {
    color: color.white,
  },
});
